import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  // Divider,
} from "@mui/material";
import { formatInTimeZone } from "date-fns-tz";


const numberFormat = (value, digit = 0) =>
  value !== undefined && value !== null
    ? Number(value).toLocaleString("en-US", { maximumFractionDigits: digit })
    : "-";

export default function CrewIncomeDialog({ open, onClose, data }) {
  if (!data || open === false) return null;

  const {
    crewCode = "",
    crewName = "",
    activeJobs = [],
    travelPerdiem_Amount = 0,
    incomeAdjustment_Amount = 0,
    totalIncome_Amount = 0,
    familyDeduction_Amount = 0,
    nonTaxableIncome_Amount = 0,
    convertedIncome_Amount = 0,
    incomeBeforeTax_Amount = 0,
    taxableIncome_Amount = 0,
    personalIncomeTax_Amount = 0,
    grossIncome_Amount = 0,
  } = data;
    
  const summaryRows = [
    {
      label: "Travel Perdiem",
      value: travelPerdiem_Amount,      
    },
    {
      label: "Income Adjustment",
      value: incomeAdjustment_Amount,
    },
    { 
      label: "Total Income", 
      value: totalIncome_Amount, 
      sx: {
        color: "success.light",
        fontWeight: "bold",
      },
    },
    { 
      label: "Family Deduction", 
      value: familyDeduction_Amount 
    },
    { 
      label: "Non-Taxable Income", 
      value: nonTaxableIncome_Amount 
    },
    { 
      label: "Converted Income", 
      value: convertedIncome_Amount 
    },
    { 
      label: "Income Before Tax", 
      value: incomeBeforeTax_Amount 
    },
    { 
      label: "Personal Income Tax", 
      value: personalIncomeTax_Amount 
    },
    { 
      label: "Gross Income", 
      value: grossIncome_Amount, 
      sx: {
        color: "success.light",
        fontWeight: "bold",
      },
    },
    { 
      label: "Taxable Income", 
      value: taxableIncome_Amount, 
      sx: {
        color: "success.light",
        fontWeight: "bold",
      },
    },
  ];

  const totalByJob = activeJobs.reduce((acc, cur) => {
    if (!cur.jobTitle) return acc;
    acc[cur.jobTitle] = (acc[cur.jobTitle] || 0) + (cur.incomeAmount || 0);
    return acc;
  }, {});
  // const totalByJobs_Amount = Object.values(totalByJob).reduce((sum, val) => sum + val, 0);
  

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold" color="primary">
          {crewCode} - {crewName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {activeJobs?.map((item, index) => (
          <React.Fragment key={index}>
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{ fontWeight: "bold", mt: index > 0 ? 3 : 0 }}
            >
              {`Job Title: ${item.jobTitle} (${item?.effectiveFrom ? formatInTimeZone(item?.effectiveFrom, 'UTC', 'dd/MM/yyyy') : ""} → ${
                item.effectiveTo ? formatInTimeZone(item?.effectiveTo, 'UTC', 'dd/MM/yyyy') : "Present"
              })`}
            </Typography>

            <Grid container spacing={1} sx={{ mt: 1, alignItems: "center" }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2">
                  <b>Period:</b> {item.start ? formatInTimeZone(item.start, 'UTC', 'dd/MM/yy') : ''} → {item.end ? formatInTimeZone(item.end, 'UTC', 'dd/MM/yy') : ''}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2">
                  <b>Active Days:</b> {item.activeDays}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2">
                  <b>Paid Days:</b> {item.paidDays}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2">
                  <b>Working Days:</b> {item.workingDays}
                </Typography>
              </Grid>              
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2">
                  <b>Standard Working Days:</b> {item.standardWorkingDays}
                </Typography>
              </Grid>
              {/* <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2">
                  <b>Non Working Days:</b> {item.nonWorkingDays}
                </Typography>
              </Grid> */}
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2">
                    <b>Income:</b>
                  </Typography>
                  <Typography
                    variant="body2"
                    color="success.light"
                    sx={{ ml: 0.5 }}
                  >
                    {numberFormat(item.incomeAmount)} ₫
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell><b>Item</b></TableCell>
                  <TableCell align="right"><b>Unit</b></TableCell>
                  <TableCell align="right"><b>Qty</b></TableCell>
                  <TableCell align="right"><b>Amount</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(item)
                  .filter(([key]) => key.endsWith("_Unit"))
                  .map(([key, value]) => {
                    const baseName = key.replace("_Unit", "");
                    const qty = item[`${baseName}_Qty`];
                    const amount = item[`${baseName}_Amount`];
                    if (!value && !amount) return null;
                    return (
                      <TableRow key={key}>
                        <TableCell>{baseName.replace(/_/g, " ")}</TableCell>
                        <TableCell align="right">{numberFormat(value)}</TableCell>
                        <TableCell align="right">{numberFormat(qty, 2)}</TableCell>
                        <TableCell align="right">{numberFormat(amount)}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>

            {/* <Divider sx={{ my: 2 }} /> */}
          </React.Fragment>
        ))}
        {Object.keys(totalByJob).length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              Summary
            </Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell><b>Item</b></TableCell>
                  <TableCell align="right"><b>Amount</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(totalByJob).map(([title, total]) => (
                  <TableRow key={title}>
                    <TableCell>{title}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {numberFormat(total)} ₫
                    </TableCell>
                  </TableRow>
                ))}                
                {/* Summary rows */}
                {summaryRows.map(({ label, value, sx }) => (
                  <TableRow key={label}>
                    <TableCell sx={{ ...sx }}>{label}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ ...sx }}
                    >
                      {numberFormat(value)} ₫
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
